import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Hourglass, FileClock, Send } from "lucide-react";
import { api, messageErreur } from "@/lib/api";

const Ind = ({ label, valeur, testId }) => (
  <div className="at-card p-3 min-w-0"><p className="at-label !normal-case !tracking-normal text-xs truncate">{label}</p><p className="text-xl font-bold mt-1" data-testid={testId}>{valeur}</p></div>
);

const Bloc = ({ titre, icone: Icone, testId, children, vide }) => (
  <section className="at-card p-4 space-y-2" data-testid={testId}>
    <p className="font-bold flex items-center gap-2"><Icone size={16} />{titre}</p>
    {vide ? <p className="text-sm at-muted">{vide}</p> : children}
  </section>
);

const Ligne = ({ f, detail, testId }) => (
  <Link to={`/fiches/${f.id}`} className="flex items-center justify-between gap-2 min-h-[44px]" data-testid={testId}>
    <span className="min-w-0"><span className="font-semibold block truncate">{f.nom}</span><span className="text-xs at-muted block truncate">{detail}</span></span>
  </Link>
);

export const PosteConfirmateur = () => {
  const [d, setD] = useState(null);
  const charger = useCallback(() => api.get("/confirmation/accueil").then((r) => setD(r.data)).catch((e) => toast.error(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);
  if (!d) return <p className="at-muted" data-testid="confirm-loading">Chargement de votre plan du jour…</p>;
  const i = d.indicateurs;
  return (
    <div className="space-y-3 mb-8 min-w-0" data-testid="confirm-station">
      <section className="at-card p-4" style={{ background: "var(--glow)" }} data-testid="day-plan">
        <p className="at-label">Plan du jour</p>
        <p className="text-lg font-bold mt-1" data-testid="day-plan-text">{d.plan_texte}</p>
        {d.sans_prochaine_action > 0 && <p className="text-sm mt-1" style={{ color: "var(--danger)" }} data-testid="no-next-action">{d.sans_prochaine_action} dossier(s) sans prochaine action</p>}
      </section>
      <Link to="/focus-confirmation" className="at-btn at-btn-primary w-full !min-h-[56px] text-base" data-testid="confirm-focus-btn"><Zap size={18} />Mode focus</Link>
      <section className="at-card p-4 flex items-center justify-between gap-3" data-testid="to-distribute">
        <p className="font-bold flex items-center gap-2"><Send size={16} />Prêts à distribuer</p>
        <span className="text-2xl font-bold" data-testid="to-distribute-count">{d.a_distribuer.length}</span>
      </section>
      {d.a_distribuer.length > 0 && <div className="at-card p-3 space-y-1">{d.a_distribuer.map((f) => <Ligne key={f.id} f={f} detail={`${f.operation} · ${f.commune || ""}`} testId={`distribute-row-${f.id}`} />)}</div>}
      <div className="grid grid-cols-3 gap-2" data-testid="confirm-indicators">
        <Ind label="Traités (30 j)" valeur={i.traites} testId="ind-treated" />
        <Ind label="Lead → distrib." valeur={i.delai_moyen_h === null ? "—" : `${i.delai_moyen_h} h`} testId="ind-delay" />
        <Ind label="Validation" valeur={i.taux_validation === null ? "—" : `${i.taux_validation} %`} testId="ind-validation" />
      </div>
      <Bloc titre={`Fiches qui stagnent (${d.nb_stagnantes})`} icone={Hourglass} testId="stagnant-block" vide={d.stagnantes.length ? null : "Aucune fiche en retard."}>
        {d.stagnantes.map((f) => <Ligne key={f.id} f={f} detail={`${f.etape_label} depuis le ${new Date(f.etape_entree_at).toLocaleDateString("fr-FR")}`} testId={`stagnant-row-${f.id}`} />)}
      </Bloc>
      <Bloc titre={`En attente de documents (${d.attente_documents.length})`} icone={FileClock} testId="docs-waiting-block" vide={d.attente_documents.length ? null : "Aucun dossier en attente."}>
        {d.attente_documents.map((f) => <Ligne key={f.id} f={f} detail={`${f.jours_restants ?? "—"} j restants · manque : ${f.manquantes.join(", ")}`} testId={`docs-waiting-row-${f.id}`} />)}
      </Bloc>
    </div>
  );
};
